import { pageLayerState } from "./page-layers";
const observers = new WeakMap<HTMLElement, { disconnect(): void }>();
export function configureBackdrop(
  layer: HTMLElement,
  theme: any,
  origin: string,
  device?: string,
) {
  observers.get(layer)?.disconnect();
  let source = "",
    ratio = 0,
    edgeColor = "";
  let revision = 0;
  const update = () => {
    const mobile = device
      ? device === "mobile"
      : matchMedia("(max-width:640px)").matches;
    const artwork = mobile ? theme.mobile || theme.desktop : theme.desktop;
    if (artwork !== source) {
      source = artwork || "";
      ratio = 0;
      edgeColor = "";
      const current = ++revision;
      if (source) {
        const image = new Image();
        image.crossOrigin = "anonymous";
        image.onload = () => {
          if (current !== revision) return;
          ratio = image.naturalHeight / image.naturalWidth;
          if ((theme.blurredBackground || "same") === "same") {
            try {
              const canvas = document.createElement("canvas");
              canvas.width = 40;
              canvas.height = 8;
              const context = canvas.getContext("2d", {
                willReadFrequently: true,
              })!;
              // The middle of the bottom edge avoids the decorative corners.
              context.drawImage(
                image,
                image.naturalWidth * 0.3,
                image.naturalHeight * 0.975,
                image.naturalWidth * 0.4,
                image.naturalHeight * 0.025,
                0,
                0,
                40,
                8,
              );
              const pixels = context.getImageData(0, 0, 40, 8).data;
              const channels: number[][] = [[], [], []];
              for (let pixel = 0; pixel < pixels.length; pixel += 4) {
                if (pixels[pixel + 3] < 128) continue;
                for (let channel = 0; channel < 3; channel++)
                  channels[channel].push(pixels[pixel + channel]);
              }
              if (channels[0].length)
                edgeColor = `rgb(${channels
                  .map((values) =>
                    Math.round(
                      values.sort((a, b) => a - b)[
                        Math.floor(values.length / 2)
                      ] *
                        (1 - (theme.darkness || 0)),
                    ),
                  )
                  .join(" ")})`;
            } catch {
              // External images without canvas access retain the saved page color.
            }
          }
          update();
        };
        image.onerror = () => {
          if (current === revision) {
            ratio = 0;
            apply();
          }
        };
        image.src = new URL(source, origin).href;
      }
    }
    apply();
  };
  const apply = () => {
    const activeDevice =
      device === "mobile" ||
      (!device && matchMedia("(max-width:640px)").matches)
        ? "mobile"
        : "desktop";
    const sharp = pageLayerState(theme, activeDevice, "background");
    const sharpHeight =
      sharp.present && !sharp.hidden ? layer.clientWidth * ratio : 0;
    layer.style.setProperty("--sharp-height", `${sharpHeight}px`);
    const mode = theme.blurredBackground || "same";
    const image = mode === "custom" ? theme.blurredImage : source;
    const backdrop = pageLayerState(theme, activeDevice, "backdrop");
    const blend =
      mode === "same" &&
      backdrop.present &&
      !backdrop.hidden &&
      !!source &&
      ratio > 0 &&
      layer.clientHeight > sharpHeight + 1 &&
      sharp.present &&
      !sharp.hidden;
    layer.style.setProperty(
      "--background-base",
      blend && edgeColor ? edgeColor : theme.secondary || "#fffaf0",
    );
    layer.style.setProperty(
      "--artwork-mask",
      blend
        ? `linear-gradient(to bottom,#000 ${Math.max(0, sharpHeight - Math.min(220, sharpHeight * 0.18))}px,transparent ${sharpHeight}px)`
        : "none",
    );
    const visible =
      activeDevice !== "mobile" &&
      backdrop.present &&
      !backdrop.hidden &&
      mode === "custom" &&
      !!image &&
      layer.clientHeight > sharpHeight + 1 &&
      (!layer.matches(".scene,.pinedale-site-background") ||
        window.innerHeight > sharpHeight + 1) &&
      (!source || ratio > 0);
    layer.style.setProperty("--blur-display", visible ? "block" : "none");
    layer.style.setProperty(
      "--blur-image",
      visible
        ? `url("${new URL(image, origin).href.replaceAll('"', "%22")}")`
        : "none",
    );
  };
  const observer = new ResizeObserver(update);
  observer.observe(layer);
  const resize = () => {
    if (layer.isConnected) update();
    else disconnect();
  };
  const disconnect = () => {
    observer.disconnect();
    window.removeEventListener("resize", resize);
  };
  window.addEventListener("resize", resize);
  observers.set(layer, { disconnect });
  update();
}
